// uploadProgressStore.ts -- in-memory progress for in-flight CDU
// (chunked direct upload) PUTs.
//
// Files >10 MB take the CDU path: browser PUTs bytes directly to S3
// via a presigned URL minted by the BFF (server/src/routes/documents.ts
// :: POST /reserve). The bytes never traverse the BFF, so the BFF
// can't report upload progress -- the browser is the ONLY observer
// of upload-byte movement. This store holds the per-document progress
// the XHR's upload.onprogress handler emits, keyed by documentId.
//
// The DigestStatus panel reads from here and synthesises an "upload"
// stage that PREPENDS the server-driven digest stages (extract /
// chunk / embed). Once the upload PUT finishes, /commit fires; the
// stage stays visible as 'complete' but the digest pipeline takes
// over for the rest of the timeline.
//
// Not persisted. An in-flight upload will lose its progress entry on
// page reload; the user-visible effect is the upload stage will
// disappear from the panel mid-upload. We could persist this but the
// XHR itself doesn't survive the reload, so the upload would die
// regardless -- nothing to recover. The catalog entry created at
// /reserve time DOES survive reload, with its uploadStatus='uploading'
// marker; that's enough for the UI to reconstruct "an upload was in
// flight" if the user reloads.
import { create } from 'zustand';
export const useUploadProgressStore = create((set) => ({
    entries: {},
    startUpload: ({ documentId, filename, totalBytes, pendingFlows }) => {
        set((s) => ({
            entries: {
                ...s.entries,
                [documentId]: {
                    documentId,
                    filename,
                    totalBytes,
                    bytesSent: 0,
                    status: 'pending',
                    startedAt: new Date().toISOString(),
                    pendingFlows,
                },
            },
        }));
    },
    updateProgress: (documentId, bytesSent) => {
        set((s) => {
            const cur = s.entries[documentId];
            if (!cur)
                return s;
            // Cap at totalBytes -- XHR sometimes reports a final value
            // slightly larger than totalBytes due to chunk overhead.
            const capped = Math.min(bytesSent, cur.totalBytes);
            // Skip the state update if bytesSent didn't change AND status
            // is already 'uploading'. Cheap belt-and-braces against panel
            // re-renders (rowSummary only cares about status, not bytes,
            // but the panel's progress bar uses bytes -- still cheap).
            if (cur.bytesSent === capped && cur.status === 'uploading')
                return s;
            return {
                entries: {
                    ...s.entries,
                    [documentId]: {
                        ...cur,
                        bytesSent: capped,
                        status: 'uploading',
                    },
                },
            };
        });
    },
    markComplete: (documentId) => {
        set((s) => {
            const cur = s.entries[documentId];
            if (!cur)
                return s;
            return {
                entries: {
                    ...s.entries,
                    [documentId]: {
                        ...cur,
                        bytesSent: cur.totalBytes,
                        status: 'complete',
                        completedAt: new Date().toISOString(),
                    },
                },
            };
        });
    },
    markFailed: (documentId, error) => {
        set((s) => {
            const cur = s.entries[documentId];
            if (!cur)
                return s;
            return {
                entries: {
                    ...s.entries,
                    [documentId]: {
                        ...cur,
                        status: 'failed',
                        error,
                        completedAt: new Date().toISOString(),
                    },
                },
            };
        });
    },
    clearEntry: (documentId) => {
        set((s) => {
            if (!s.entries[documentId])
                return s;
            const { [documentId]: _drop, ...rest } = s.entries;
            return { entries: rest };
        });
    },
}));
/** Hook: a single entry. Returns undefined when no upload is/was
 *  tracked for this documentId. */
export function useUploadProgress(documentId) {
    return useUploadProgressStore((s) => s.entries[documentId]);
}
